<template>
  <div>
    <Head :title="`Detail Kelurahan ${kelurahan}`" />

    <!-- HEADER -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold">
        Detail Kelurahan {{ kelurahan }}
      </h1>
      <p class="text-gray-600">
        Kecamatan {{ kecamatan }}
      </p>
    </div>

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full p-4 font-semibold bg-gray-200">Filter</div>
      <div class="flex flex-wrap w-full p-6">
        <div class="w-full font-semibold lg:w-3/12">Periode Beasiswa:</div>

        <v-select
          v-model="form.periode"
          :options="periodes"
          class="w-full lg:w-9/12"
          :reduce="p => p.id"
          label="nama"
          placeholder="Pilih Periode"
        />
      </div>
    </div>

    <!-- TABEL MAHASISWA -->
    <div class="w-full mb-10 bg-white border rounded shadow">
      <div class="flex justify-between px-4 py-3 font-semibold bg-gray-100 border-b">
        Daftar Mahasiswa
        <div class="flex justify-end mb-4">
          <a
            :href="route('report.kelurahan.detail.export', {
              kecamatan,
              kelurahan,
              periode: form.periode
            })"
            class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700"
          >
            Export Excel
          </a>
        </div>
      </div>

      <div class="overflow-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b bg-gray-50">
              <!-- NAMA -->
              <th
                class="p-2 text-left border cursor-pointer hover:bg-gray-100"
                @click="toggleSort('nama')"
              >
                Nama
                <span v-if="sort.field === 'nama'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- NIM -->
              <th class="p-2 text-left border">NIM</th>

              <!-- UNIVERSITAS -->
              <th
                class="p-2 text-left border cursor-pointer hover:bg-gray-100"
                @click="toggleSort('universitas')"
              >
                Kampus
                <span v-if="sort.field === 'universitas'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- PRODI -->
              <th class="p-2 text-left border">Prodi</th>

              <!-- SEMESTER -->
              <th
                class="p-2 text-center border cursor-pointer hover:bg-gray-100"
                @click="toggleSort('semester')"
              >
                Semester
                <span v-if="sort.field === 'semester'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- PENGHASILAN ORTU -->
              <th
                class="p-2 text-center border cursor-pointer hover:bg-gray-100"
                @click="toggleSort('penghasilan')"
              >
                Penghasilan Ortu
                <span v-if="sort.field === 'penghasilan'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- JK -->
              <th class="p-2 text-center border">JK</th>

              <!-- ALAMAT -->
              <th class="p-2 text-left border">Alamat</th>
            </tr>
          </thead>


          <tbody>
            <tr
              v-for="(mhs, i) in sortedMahasiswa"
              :key="i"
              class="border-b hover:bg-gray-50"
            >
              <td class="p-2 border">{{ mhs.nama }}</td>
              <td class="p-2 border">{{ mhs.nim }}</td>
              <td class="p-2 border">{{ mhs.universitas }}</td>
              <td class="p-2 border">{{ mhs.prodi }}</td>
              <td class="p-2 text-center border">{{ mhs.semester }}</td>

              <!-- FORMAT RUPIAH -->
              <td class="p-2 text-center border">{{ formatRupiah(mhs.penghasilan) }}</td>

              <td class="p-2 text-center border">{{ mhs.jk }}</td>
              <td class="p-2 border">{{ mhs.alamat }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-xl font-bold">
        Grafik Jumlah Mahasiswa Berdasarkan Kampus
      </h2>

      <apexchart
        width="100%"
        height="420"
        type="bar"
        :options="kampusChartOptions"
        :series="kampusSeries"
      />
    </div>
  </div>
</template>
<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  components: {
    Head,
    apexchart: VueApexCharts,
  },
  layout: Layout,

  props: {
    kecamatan: String,
    kelurahan: String,
    mahasiswa: Array,
    filters: Object,
    periodes: Array,
  },

  data() {
    return {
      form: {
        periode: parseInt(this.filters.periode) || null,
      },
      sort: {
        field: 'universitas',
        direction: 'asc',
      },
    }
  },

  computed: {
    // menghitung total mahasiswa per kampus
    kampusCount() {
      const result = {}
      this.mahasiswa.forEach(m => {
        result[m.universitas] = (result[m.universitas] || 0) + 1
      })
      return result
    },

    kampusLabels() {
      return Object.keys(this.kampusCount)
    },

    kampusSeries() {
      return [{
        name: 'Jumlah',
        data: Object.values(this.kampusCount),
      }]
    },
    sortedMahasiswa() {
      return [...this.mahasiswa].sort((a, b) => {
        let field = this.sort.field
        let dir = this.sort.direction === 'asc' ? 1 : -1

        let x = a[field]
        let y = b[field]

        // khusus numeric (semester & penghasilan)
        if (field === 'semester' || field === 'penghasilan') {
          x = Number(x)
          y = Number(y)
        }

        if (x < y) return -1 * dir
        if (x > y) return 1 * dir
        return 0
      })
    },


    kampusChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        xaxis: {
          categories: this.kampusLabels,
          labels: { rotate: -45 },
        },
        dataLabels: { enabled: true },
      }
    },
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        const query = pickBy(this.form)

        this.$inertia.get(
          this.route('report.kelurahan.detail', {
            kecamatan: this.kecamatan,
            kelurahan: this.kelurahan,
          }),
          query,
          {
            preserveState: true,
            preserveScroll: true,
          },
        )
      }, 150),
    },
  },
  methods: {
    formatRupiah(value) {
      if (!value) return '-'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(value)
    },
    toggleSort(field) {
      if (this.sort.field === field) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.field = field
        this.sort.direction = 'asc'
      }
    },

  },
}
</script>
